import { Component, inject } from '@angular/core';
import { FormsModule } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogRef } from "@angular/material/dialog";
import { MatFormField, MatLabel } from "@angular/material/input";
import { MatOption } from "@angular/material/core";
import { MatSelect } from "@angular/material/select";
import { MatTab, MatTabGroup } from "@angular/material/tabs";
import { SelectFilterComponent } from "../../components/select-filter/select-filter.component";
import { SharedPipesModule } from "../../shared/modules/shared-pipes.module";
import { AppScope } from "../../services/app_scope.service";
import { DateTimeInstanceService } from "../../services/date-time-instance.service";
import { BaseComponent } from "../../shared/base.component";
import { combineLatest, EMPTY, takeUntil } from "rxjs";
import { OptionalCalendar } from "../../_typing/date-time-period";
import { DateTimePeriodService } from "../../services/date-time-period.service";
import { MatSnackBarRef } from "@angular/material/snack-bar";
import { ConfirmSnackbarComponent } from "../../notifications/snackbar/confirm-snackbar/confirm-snackbar.component";
import { catchError, take, tap } from "rxjs/operators";
import { FormDialogService } from "../../services/form-dialog.service";
import { NotificationService } from "../../services/notification.service";
import { KeyMap, ModelID } from "../../_typing/generic-types";
import { Configuration, PageDatePeriodValue } from "../../_models/configuration";
import { ConfigDataService } from "../../data/config-data.service";
import { deepCopy } from "../../lib/utils";

@Component({
    selector: 'src-static-page-form',
    imports: [
        FormsModule,
        MatDialogContent,
        MatFormField,
        MatLabel,
        MatOption,
        MatSelect,
        MatTab,
        MatTabGroup,
        SelectFilterComponent,
        SharedPipesModule,
    ],
    templateUrl: './static-page-form.component.html'
})
export class StaticPageFormComponent extends BaseComponent {
    public data: any = inject(MAT_DIALOG_DATA);
    title: string;
    pageUrlSegment: string;
    configPageDefaults: KeyMap<PageDatePeriodValue>;
    configPageId: ModelID;
    pageDatePeriod: PageDatePeriodValue;

    defaultDtp: { calendar: string; range: string; sample_period: string };
    public ranges: string[];
    public rangeGroups: { label: string; children: string[] }[];
    public calendars: OptionalCalendar[];
    samplePeriods: string[];
    samplePeriodGroups: { label: string; children: string[] }[];

    private _formDirty: boolean = false;

    constructor(protected appScope: AppScope,
        protected dateTimePeriodService: DateTimePeriodService,
        private dateInst: DateTimeInstanceService,
        private formDialogService: FormDialogService,
        private dialogRef: MatDialogRef<any>,
        private notification: NotificationService,
        private configDataService: ConfigDataService) {
        super();
        this.configPageDefaults = this.appScope.config_name_map?.page_date_period?.value;
        this.configPageId = this.appScope.config_name_map?.page_date_period?.id;
        this.pageUrlSegment = this.data.pageUrlSegment;
        this.title = this.data.title;
    }

    ngOnInit() {

        combineLatest([this.dateInst.dateTimePeriodInitialised$, this.appScope.configDictChanged]).pipe(takeUntil(this.onDestroy)).subscribe(() => {
            let dtp = this.dateInst.dtp;
            this.ranges = this.dateTimePeriodService.getRangeNames();
            this.rangeGroups = this.dateTimePeriodService.getRangeGroups();

            this.calendars = this.dateTimePeriodService.calendars;

            this.samplePeriods = this.dateTimePeriodService.getSamplePeriodNames();
            this.samplePeriodGroups = this.dateTimePeriodService.getSamplePeriodGroups();

            dtp = this.dateTimePeriodService.getPageDtpDefault(dtp, this.pageUrlSegment, true);
            this.dateInst.dateTimePeriodChanged$.next(dtp);

            this.defaultDtp = {
                calendar: dtp.calendar,
                range: dtp.range,
                sample_period: dtp.sample_period.name
            }

            if (this.pageUrlSegment && this.configPageDefaults?.[this.pageUrlSegment]) {
                this.pageDatePeriod = this.configPageDefaults[this.pageUrlSegment];
            } else {
                this.pageDatePeriod = new PageDatePeriodValue();
            }

            this._formDirty = false;
        })
    }

    markDirty() {
        this._formDirty = true;
    }

    private _newConfigItem() {
        const accountId = this.appScope.active_account_id;
        let c = new Configuration();
        c.attributes.name = 'page_date_period';
        c.attributes.config_type = 'json';
        c.relationships.account = { data: { id: accountId, type: 'account' } }
        return c;
    }

    async save() {
        const confirm = await this.formDialogService.confirm('Please note: "Save" will push the attribute changes you have made into production.')
        if (!confirm) {
            return;
        }
        const accountId = this.appScope.active_account_id;

        const calendarEntry = this.dateTimePeriodService.calendar_dict[this.defaultDtp.calendar];
        if (!calendarEntry) {
            this.notification.openError('Selected calendar is invalid.');
            return;
        }
        this.pageDatePeriod.calendar_id = calendarEntry.id;
        this.pageDatePeriod.range = this.defaultDtp.range;
        this.pageDatePeriod.sample_period = this.defaultDtp.sample_period;

        const updatedDefaults = Object.assign(deepCopy(this.configPageDefaults) || {}, { [this.pageUrlSegment]: this.pageDatePeriod });
        let configItem = this.appScope.config_data.find(item => item.id === this.configPageId) || this._newConfigItem();
        configItem.attributes.value = JSON.stringify(updatedDefaults);

        let isNew = false;
        //If using WIREBaseConfig then set to new item on current account
        if (accountId !== configItem.relationships.account?.data?.id) {
            isNew = true;
            configItem.relationships.account.data.id = accountId;
            delete configItem.id;
        }

        this.configDataService.upsertConfig(configItem)
            .pipe(
                takeUntil(this.onDestroy),
                tap((result) => {
                    if (isNew) {
                        this.appScope.config_data.push(result.data);
                    } else {
                        const currentConfigIndex = this.appScope.config_data.findIndex(item => item.id === this.configPageId)
                        this.appScope.config_data[currentConfigIndex] = result.data;
                    }
                    this.appScope.config_name_map['page_date_period'] = {
                        id: result.data.id,
                        value: result.data.attributes.value
                    };
                    this.configPageDefaults = updatedDefaults;
                }),
                catchError((e: Error) => {
                    this.notification.openError('There was a problem saving this page.');
                    console.error("ERROR: StaticPageForm (save)", e);
                    return EMPTY;
                })
            ).subscribe(() => {
                let newDtp = this.dateTimePeriodService.getDTP(this.defaultDtp.range, false, this.defaultDtp.calendar);
                newDtp.sample_period = this.dateTimePeriodService.sample_dict[this.defaultDtp.sample_period];
                this.dialogRef.close(newDtp);
            });
    }

    onCloseClick(): void {
        if (!this._formDirty) {
            this.dialogRef.close();
            return;
        }
        let confirmNotification: MatSnackBarRef<ConfirmSnackbarComponent>;
        confirmNotification = this.notification.openConfirm('You have unsaved changes. Click OK to discard them, or Cancel to go back.');
        confirmNotification.onAction().pipe(take(1)).subscribe((): void => {
            this.dialogRef.close();
        });
    }
}
